import { Box, Grid, Typography, useTheme } from "@mui/material";
import { isPOBoxAddress } from "encamp-shared/src/utils/address";
import React from "react";

import { AgencyDetails } from "../Forms/AgencyDetails";
import { AgencyMap } from "./AgencyMap";
import { DistanceDisplay } from "./DistanceDisplay";

interface AgencyFragment {
  id: string;
  name?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  city?: string | null;
  state?: string | null;
  county?: string | null;
  mailingAddress?: string | null;
}

interface AgencyPickerLayoutProps<T extends AgencyFragment> {
  selectedAgency?: T | null;
  facilityLatitude?: number | null;
  facilityLongitude?: number | null;
  children: React.ReactNode; // The picker component
  pickerDescription?: string;
}

export function AgencyPickerLayout<T extends AgencyFragment>({
  selectedAgency,
  facilityLatitude,
  facilityLongitude,
  children,
  pickerDescription,
}: AgencyPickerLayoutProps<T>) {
  const theme = useTheme();
  const isAddressPOBox = (selectedAgency && isPOBoxAddress(selectedAgency.mailingAddress)) ?? false;

  return (
    <Grid container spacing={3}>
      {/* Left side: Map or PO Box Image */}
      <Grid size={{ xs: 12, md: 4 }}>
        {selectedAgency ? (
          <>
            {isAddressPOBox ? (
              <>
                <Box
                  sx={{
                    height: 300,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: theme.palette.grey[50],
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.grey[300]}`,
                  }}
                >
                  <img
                    src="/agency-po-box.png"
                    alt="PO Box Address"
                    style={{
                      maxWidth: "50%",
                      maxHeight: "50%",
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: "center",
                      mt: -3,
                      fontSize: "1.2rem",
                      color: theme.palette.grey[600],
                    }}
                  >
                    Agency PO Box
                  </Typography>
                </Box>
              </>
            ) : (
              <>
                <AgencyMap
                  facilityLatitude={facilityLatitude}
                  facilityLongitude={facilityLongitude}
                  agencyLatitude={selectedAgency.latitude}
                  agencyLongitude={selectedAgency.longitude}
                  height={300}
                />

                <DistanceDisplay
                  sx={{
                    color: theme.palette.grey[600],
                  }}
                  facilityCoordinates={
                    facilityLatitude && facilityLongitude
                      ? {
                          latitude: facilityLatitude,
                          longitude: facilityLongitude,
                        }
                      : undefined
                  }
                  agencyCoordinates={
                    selectedAgency.latitude && selectedAgency.longitude
                      ? {
                          latitude: selectedAgency.latitude,
                          longitude: selectedAgency.longitude,
                        }
                      : undefined
                  }
                  isPOBoxAddress={isAddressPOBox}
                />
              </>
            )}
          </>
        ) : (
          // Placeholder when no agency is selected
          <Box
            sx={{
              height: 300,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: theme.palette.grey50.main,
              borderRadius: 1,
              border: `1px solid ${theme.palette.grey[300]}`,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                textAlign: "center",
                color: theme.palette.grey[500],
                fontSize: "0.875rem",
              }}
            >
              No agency selected
            </Typography>
          </Box>
        )}
      </Grid>

      {/* Right side: Picker and Details */}
      <Grid size={{ xs: 12, md: 8 }}>
        {children}
        {pickerDescription && (
          <Grid size={{ xs: 12 }}>
            <Typography variant="body2" color="text.secondary">
              {pickerDescription}
            </Typography>
          </Grid>
        )}
        {selectedAgency && <AgencyDetails agency={selectedAgency} />}
      </Grid>
    </Grid>
  );
}
